import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import DataTable from '../components/DataTable';
import { currenciesAPI } from '../services/mastersAPI';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const Currencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    symbol: '',
    decimalPlaces: 2,
    exchangeRate: 1.0,
    isBase: false,
    isActive: true
  });

  useEffect(() => {
    fetchCurrencies();
  }, [currentPage, searchTerm]);

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await currenciesAPI.getCurrencies({
        page: currentPage,
        limit: 10,
        search: searchTerm
      });
      setCurrencies(response.currencies);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCurrency = async (e) => {
    e.preventDefault();
    try {
      await currenciesAPI.createCurrency(formData);
      toast.success('Currency created successfully');
      setShowAddModal(false);
      resetForm();
      fetchCurrencies();
    } catch (error) {
      console.error('Error creating currency:', error);
      toast.error(error.response?.data?.message || 'Failed to create currency');
    }
  };

  const handleEditCurrency = async (e) => {
    e.preventDefault();
    try {
      await currenciesAPI.updateCurrency(selectedCurrency._id, formData);
      toast.success('Currency updated successfully');
      setShowEditModal(false);
      resetForm();
      setSelectedCurrency(null);
      fetchCurrencies();
    } catch (error) {
      console.error('Error updating currency:', error);
      toast.error(error.response?.data?.message || 'Failed to update currency');
    }
  };

  const handleDeleteCurrency = (currency) => {
    setCurrencyToDelete(currency);
    setShowDeleteModal(true);
  };

  const confirmDeleteCurrency = async () => {
    if (!currencyToDelete) return;

    try {
      setLoading(true);
      await currenciesAPI.deleteCurrency(currencyToDelete._id);
      
      // Update the currencies list
      setCurrencies(currencies.filter(currency => currency._id !== currencyToDelete._id));
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setCurrencyToDelete(null);
      
      toast.success('Currency deleted successfully');
    } catch (error) {
      console.error('Error deleting currency:', error);
      toast.error(error.response?.data?.message || 'Failed to delete currency');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCurrency = (currency) => {
    setSelectedCurrency(currency);
    setShowViewModal(true);
  };

  const handleEditClick = (currency) => {
    setSelectedCurrency(currency);
    setFormData({
      name: currency.name,
      code: currency.code,
      symbol: currency.symbol,
      decimalPlaces: currency.decimalPlaces,
      exchangeRate: currency.exchangeRate,
      isBase: currency.isBase,
      isActive: currency.isActive
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      symbol: '',
      decimalPlaces: 2,
      exchangeRate: 1.0,
      isBase: false,
      isActive: true
    });
  };

  const columns = [
    {
      key: 'symbol',
      header: 'Symbol',
      render: (value) => (
        <span className="text-lg font-semibold text-gray-900">{value}</span>
      )
    },
    { key: 'name', header: 'Currency Name' },
    { key: 'code', header: 'Code' },
    { key: 'decimalPlaces', header: 'Decimal Places' },
    {
      key: 'exchangeRate',
      header: 'Exchange Rate',
      render: (value) => value.toFixed(4)
    },
    {
      key: 'isBase',
      header: 'Base Currency',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Base' : 'Secondary'}
        </span>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, item) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewCurrency(item);
            }}
            className="text-blue-600 hover:text-blue-900 p-1 rounded-lg hover:bg-blue-50 transition-colors duration-200"
            title="View"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(item);
            }}
            className="text-indigo-600 hover:text-indigo-900 p-1 rounded-lg hover:bg-indigo-50 transition-colors duration-200"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCurrency(item);
            }}
            className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50 transition-colors duration-200"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];



  if (loading && currencies.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading currencies..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Currencies Master
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Manage currencies for global financial operations and pricing
                </p>
              </div>
              <div className="flex-shrink-0">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Currency
                </motion.button>
              </div>
            </div>
          </div>

        {/* Search */}
        <div className="mt-6">
          <div className="flex items-center">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search currencies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Currencies Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100/50">
            <h2 className="text-lg font-semibold text-gray-900">Currencies</h2>
            <p className="text-sm text-gray-600 mt-1">Manage currencies for financial operations</p>
          </div>

          <div className="overflow-x-auto">
            {/* Card Grid Layout */}
            {currencies.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {currencies.map((currency, index) => (
                  <motion.div
                    key={currency._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  >
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-4 relative overflow-hidden">
                      <div className="absolute inset-0 bg-black/10"></div>
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                            <span className="text-2xl">
                              {currency.symbol || currency.code?.charAt(0) || '💰'}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">{currency.name}</h3>
                            <p className="text-white/80 text-xs">Currency</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {currency.isBase && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-100 border border-yellow-400/30">
                              Base
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            currency.isActive
                              ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
                              : 'bg-gray-500/20 text-gray-100 border border-gray-400/30'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                              currency.isActive ? 'bg-emerald-400' : 'bg-gray-400'
                            }`}></div>
                            {currency.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-4 space-y-3">
                      {/* Currency Details */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Code</p>
                          <p className="text-gray-900 font-semibold">{currency.code || '-'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Symbol</p>
                          <p className="text-gray-900 font-semibold">{currency.symbol || '-'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Exchange Rate</p>
                          <p className="text-gray-900 font-semibold">{currency.exchangeRate?.toFixed(4) || '1.0000'}</p>
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Decimal Places</p>
                          <p className="text-gray-900 font-semibold">{currency.decimalPlaces || 2}</p>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2">
                        <span>Created {new Date(currency.createdAt).toLocaleDateString('en-GB')}</span>
                        <span>{currency.isBase ? 'Base Currency' : 'Regular Currency'}</span>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="px-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleViewCurrency(currency)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-xs font-medium"
                          >
                            <EyeIcon className="h-3.5 w-3.5" />
                            <span>View</span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleEditClick(currency)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-xs font-medium"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                            <span>Edit</span>
                          </motion.button>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteCurrency(currency)}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200 text-xs font-medium"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          <span>Delete</span>
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                  <CurrencyDollarIcon className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No currencies found' : 'No currencies yet'}
                </h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  {searchTerm
                    ? 'Try adjusting your search terms or clear the search to see all currencies.'
                    : 'Get started by adding currencies to support global financial operations.'
                  }
                </p>
                {!searchTerm && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      resetForm();
                      setShowAddModal(true);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Your First Currency
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Add Currency Modal */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleAddCurrency}>
                  <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <PlusIcon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Add New Currency</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="US Dollar"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.code}
                          onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono"
                          placeholder="USD"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Symbol *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.symbol}
                          onChange={(e) => setFormData({...formData, symbol: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 shadow-sm text-center text-lg font-semibold"
                          placeholder="$"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Decimal Places
                        </label>
                        <select
                          value={formData.decimalPlaces}
                          onChange={(e) => setFormData({...formData, decimalPlaces: parseInt(e.target.value)})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Exchange Rate (Base = 1.0)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0.0001"
                          value={formData.exchangeRate}
                          onChange={(e) => setFormData({...formData, exchangeRate: parseFloat(e.target.value)})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Currency Settings</h4>
                      
                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                        <div>
                          <label htmlFor="addIsBase" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Base Currency
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Exchange Rate will be set to 1.0</p>
                        </div>
                        <input
                          type="checkbox"
                          id="addIsBase"
                          checked={formData.isBase}
                          onChange={(e) => setFormData({...formData, isBase: e.target.checked})}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                        <div>
                          <label htmlFor="addIsActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Active Status
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Currency available for use</p>
                        </div>
                        <input
                          type="checkbox"
                          id="addIsActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                          className="h-5 w-5 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 border border-transparent rounded-xl hover:from-emerald-600 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                      >
                        Create Currency
                      </motion.button>
                    </div>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Currency Modal */}
        <AnimatePresence>
          {showEditModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowEditModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleEditCurrency}>
                  <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <PencilIcon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white">Edit Currency</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm"
                          placeholder="US Dollar"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Currency Code *
                        </label>
                        <input
                          type="text"
                          required
                          maxLength="3"
                          value={formData.code}
                          onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono"
                          placeholder="USD"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Symbol *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.symbol}
                          onChange={(e) => setFormData({...formData, symbol: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm text-center text-lg font-semibold"
                          placeholder="$"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Decimal Places
                        </label>
                        <select
                          value={formData.decimalPlaces}
                          onChange={(e) => setFormData({...formData, decimalPlaces: parseInt(e.target.value)})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm"
                        >
                          <option value="0">0</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Exchange Rate (Base = 1.0)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0.0001"
                          value={formData.exchangeRate}
                          onChange={(e) => setFormData({...formData, exchangeRate: parseFloat(e.target.value)})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 shadow-sm font-mono"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Currency Settings</h4>
                      
                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                        <div>
                          <label htmlFor="editIsBase" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Base Currency
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Exchange Rate will be set to 1.0</p>
                        </div>
                        <input
                          type="checkbox"
                          id="editIsBase"
                          checked={formData.isBase}
                          onChange={(e) => setFormData({...formData, isBase: e.target.checked})}
                          className="h-5 w-5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200">
                        <div>
                          <label htmlFor="editIsActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Active Status
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Currency available for use</p>
                        </div>
                        <input
                          type="checkbox"
                          id="editIsActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                          className="h-5 w-5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 border border-transparent rounded-xl hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                      >
                        Update Currency
                      </motion.button>
                    </div>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Currency Modal */}
        <AnimatePresence>
          {showViewModal && selectedCurrency && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowViewModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <EyeIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Currency Details</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setShowViewModal(false);
                          handleEditClick(selectedCurrency);
                        }}
                        className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
                      >
                        <PencilIcon className="h-5 w-5 text-white" />
                      </motion.button>
                      <button
                        type="button"
                        onClick={() => setShowViewModal(false)}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                      >
                        <XMarkIcon className="h-6 w-6 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Currency Header Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 mb-6 border border-emerald-100"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-4xl">
                          {selectedCurrency.symbol}
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{selectedCurrency.name}</h2>
                          <p className="text-gray-600 text-sm">
                            {selectedCurrency.code} • {selectedCurrency.decimalPlaces} decimal places
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                          selectedCurrency.isBase 
                            ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                            : 'bg-gray-100 text-gray-800 border border-gray-200'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            selectedCurrency.isBase ? 'bg-blue-500' : 'bg-gray-500'
                          }`}></div>
                          {selectedCurrency.isBase ? 'Base Currency' : 'Secondary'}
                        </span>
                        <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                          selectedCurrency.isActive 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            selectedCurrency.isActive ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          {selectedCurrency.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Currency Information Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-emerald-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        Basic Information
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Currency Name</span>
                          <span className="text-sm font-semibold text-gray-900">{selectedCurrency.name}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Code</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
                            {selectedCurrency.code}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Symbol</span>
                          <span className="text-lg font-bold text-gray-900 bg-teal-100 text-teal-800 px-3 py-1 rounded">
                            {selectedCurrency.symbol}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Decimal Places</span>
                          <span className="text-sm font-semibold text-gray-900 bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {selectedCurrency.decimalPlaces}
                          </span>
                        </div>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-teal-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Exchange & Status
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Exchange Rate</span>
                          <span className="text-sm font-semibold text-gray-900 font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            {selectedCurrency.exchangeRate.toFixed(4)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Currency Type</span>
                          <span className={`text-sm font-semibold px-2 py-1 rounded ${
                            selectedCurrency.isBase 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedCurrency.isBase ? 'Base Currency' : 'Secondary Currency'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Status</span>
                          <span className={`text-sm font-semibold px-2 py-1 rounded ${
                            selectedCurrency.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedCurrency.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Created</span>
                          <span className="text-sm text-gray-900">
                            {new Date(selectedCurrency.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && currencyToDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <TrashIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Delete Currency
                    </h3>
                    <p className="text-red-100 mb-4">
                      Are you sure you want to delete <strong className="text-white">{currencyToDelete.name}</strong> ({currencyToDelete.code})?
                    </p>
                    
                    <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-4 mb-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-300 mr-3 flex-shrink-0" />
                        <div className="text-left">
                          <p className="text-white font-semibold">Warning!</p>
                          <p className="text-red-100 text-sm">This will remove the currency from all plans and affect existing pricing. This action cannot be undone.</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-red-200 text-sm">
                      This action is permanent and cannot be reversed.
                    </p>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={confirmDeleteCurrency}
                    className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-pink-500 border border-transparent rounded-xl hover:from-red-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Delete Currency
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default Currencies;